import type { AriaAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';

import { Card } from '@/components/Card';
import { cn } from '@/lib/utils';

import { type CardIconColor } from './cardIconColors';

/**
 * Karta metryki (KPI) — kafelek pulpitu HR i innych ekranów statystyk.
 * 1:1 z Figmą „08.6 · Cards & widgets / Metric card": płaska ikona 18px + etykieta
 * (Label 13/500, bez uppercase), wartość H1 (30px semibold), opcjonalna stopka.
 * Shell dziedziczy z `Card` (border 1px + Elevation/Subtle + radius 12), padding 20px.
 *
 *   <StatCard label="Pozostały urlop" value="15 dni" icon={CalendarOff} />
 *   <StatCard label="Wnioski w tym miesiącu" value={42} trend={{ direction: 'up', value: '+12%' }} />
 */
export type StatCardColor = CardIconColor;

interface StatCardProps {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  /**
   * Zachowane dla zgodności API (wiele call-site'ów podaje kolor). Wg Figmy karta metryki
   * ma ikonę NEUTRALNĄ — kolor nie jest już renderowany jako kafelek. Prop jest no-opem.
   */
  iconColor?: StatCardColor;
  hint?: ReactNode;
  trend?: { direction: 'up' | 'down' | 'flat'; value: string | number; positive?: boolean };
  onClick?: () => void;
  className?: string;
  'data-testid'?: string;
  'aria-current'?: AriaAttributes['aria-current'];
}

export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  trend,
  onClick,
  className,
  'data-testid': testId,
  'aria-current': ariaCurrent,
}: StatCardProps) {
  const TrendIcon =
    trend?.direction === 'up' ? ArrowUp : trend?.direction === 'down' ? ArrowDown : Minus;
  // domyślnie: up=zielony(sukces), down=czerwony(błąd); nadpisywalne przez trend.positive
  const trendPositive = trend?.positive ?? trend?.direction === 'up';
  const trendColor =
    trend?.direction === 'flat'
      ? 'text-text-muted'
      : trendPositive
        ? 'text-[rgb(var(--color-success))]'
        : 'text-[rgb(var(--color-error))]';

  return (
    <Card
      padding="none"
      data-testid={testId}
      aria-current={ariaCurrent}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        'flex flex-col gap-3 p-5',
        onClick &&
          'cursor-pointer transition-colors hover:border-accent-400 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600',
        className,
      )}
    >
      {/* Nagłówek: płaska ikona (neutralna) + etykieta (Label 13/500, bez uppercase). */}
      <div className="flex items-center gap-2">
        {Icon && (
          <Icon className="h-[18px] w-[18px] shrink-0 text-text-secondary" aria-hidden="true" />
        )}
        <p className="min-w-0 flex-1 truncate text-[13px] font-medium leading-5 text-text-secondary">
          {label}
        </p>
      </div>

      {/* Wartość (H1 30px semibold) + opcjonalna pigułka trendu. */}
      <div className="flex items-end justify-between gap-3">
        <p className="text-[30px] font-semibold leading-[38px] tracking-[-0.6px] text-text-primary">
          {value}
        </p>
        {trend && (
          <div className={cn('inline-flex items-center gap-1 text-[13px] font-medium', trendColor)}>
            <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {trend.value}
          </div>
        )}
      </div>

      {/* Stopka (Body/Small, text-muted). */}
      {hint && <p className="truncate text-[13px] leading-5 text-text-muted">{hint}</p>}
    </Card>
  );
}

export default StatCard;
